import { useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { PhoneFrame } from './PhoneFrame';
import './ScenarioSwapDemo.css';

type SceneKey = 'chat' | 'calendar' | 'expenses';

const SCENE_ORDER: SceneKey[] = ['chat', 'calendar', 'expenses'];

const HERO_SCENES: Record<string, SceneKey> = {
  chat: 'chat',
  messages: 'chat',
  message: 'chat',
  'chat-tone': 'chat',
  toxic: 'chat',
  calendar: 'calendar',
  'calendar-weekend': 'calendar',
  schedule: 'calendar',
  expenses: 'expenses',
  expense: 'expenses',
  'expenses-price': 'expenses',
  'expenses-snap': 'expenses',
  'expenses-solo': 'expenses',
  'expenses-keyword': 'expenses',
};

function resolveScene(hero?: string): SceneKey {
  if (!hero) return 'chat';
  return HERO_SCENES[hero.trim().toLowerCase()] ?? 'chat';
}

type SceneCopy = {
  label: string;
  kicker: string;
  title: ReactNode;
  subtitle: string;
  meta: string;
};

const SCENE_COPY: Record<SceneKey, SceneCopy> = {
  chat: {
    label: 'Chat',
    kicker: 'Messaging',
    title: (
      <>
        Co-parent chats,
        <br />
        kept <span className='ss-accent'>calm.</span>
      </>
    ),
    subtitle:
      'Documented messaging with an optional Temperature Check, so heated texts do not go out as-is. Every thread stays on the record — solo or together.',
    meta: 'Read receipts on · export anytime',
  },
  calendar: {
    label: 'Calendar',
    kicker: 'Custody calendar',
    title: (
      <>
        Parenting time,
        <br />
        kept in <span className='ss-accent'>sync.</span>
      </>
    ),
    subtitle:
      'A shared custody calendar both parents can actually follow. School nights, handoffs, and trades land in one place — solo or together.',
    meta: 'Works solo or with both parents',
  },
  expenses: {
    label: 'Expenses',
    kicker: 'Shared expenses',
    title: (
      <>
        Split child
        <br />
        expenses <span className='ss-accent'>easily.</span>
      </>
    ),
    subtitle:
      'Track receipts, request reimbursements, and keep a clear record of who paid what. Works solo or with both parents.',
    meta: '50/50 or custom splits',
  },
};

const WEEKDAYS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

const CALENDAR_DAYS: Array<number | null> = [
  null,
  null,
  null,
  null,
  ...Array.from({ length: 31 }, (_, index) => index + 1),
];

const CALENDAR_TONES: Record<number, 'you' | 'maya' | 'trade'> = {
  2: 'you',
  3: 'you',
  4: 'you',
  9: 'maya',
  10: 'maya',
  11: 'maya',
  16: 'trade',
  17: 'trade',
  18: 'trade',
  23: 'you',
  24: 'you',
  25: 'you',
};

const TODAY = 21;

function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(
    () =>
      typeof window !== 'undefined' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );

  useEffect(() => {
    const query = window.matchMedia('(prefers-reduced-motion: reduce)');
    const onChange = () => setReduced(query.matches);
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);

  return reduced;
}

function ChatScene() {
  return (
    <div className='ss-scene ss-chat'>
      <header className='ss-topbar'>
        <span className='ss-topbar__avatar' aria-hidden='true'>
          M
        </span>
        <div className='ss-topbar__text'>
          <p className='ss-topbar__title'>Maya</p>
          <p className='ss-topbar__sub'>Chat · on the record</p>
        </div>
        <span className='ss-topbar__dot' aria-hidden='true' />
      </header>

      <div className='ss-chat__thread'>
        <p className='ss-chat__day'>Today</p>

        <div className='ss-bubble ss-bubble--in'>
          Can we swap Friday? I have a work thing.
        </div>

        <div className='ss-bubble ss-bubble--out'>
          Works for me. I&apos;ll pick them up at 5:30.
          <span className='ss-bubble__receipt'>Read ✓</span>
        </div>

        <p className='ss-chat__note'>Temperature check · calm</p>

        <div className='ss-bubble ss-bubble--in'>Thank you — noted.</div>
      </div>

      <div className='ss-chat__composer'>
        <span>Message</span>
        <span className='ss-chat__send' aria-hidden='true'>
          ↑
        </span>
      </div>
    </div>
  );
}

function CalendarScene() {
  return (
    <div className='ss-scene ss-calendar'>
      <header className='ss-calendar__head'>
        <div>
          <p className='ss-kicker'>Shared custody</p>
          <p className='ss-scene-title'>October 2026</p>
        </div>
        <div className='ss-legend' aria-hidden='true'>
          <span className='ss-legend__item'>
            <span className='ss-legend__dot ss-legend__dot--you' />
            You
          </span>
          <span className='ss-legend__item'>
            <span className='ss-legend__dot ss-legend__dot--maya' />
            Maya
          </span>
        </div>
      </header>

      <div
        className='ss-month'
        role='img'
        aria-label='Mini custody calendar for October 2026: teal days are yours, cream days are Maya’s, outlined days are an approved trade.'
      >
        {WEEKDAYS.map((weekday, index) => (
          <span key={`weekday-${index}`} className='ss-month__weekday'>
            {weekday}
          </span>
        ))}
        {CALENDAR_DAYS.map((day, index) => {
          if (day === null) {
            return (
              <span
                key={`empty-${index}`}
                className='ss-month__day ss-month__day--empty'
              />
            );
          }
          const tone = CALENDAR_TONES[day];
          const classes = [
            'ss-month__day',
            tone ? `ss-month__day--${tone}` : '',
            day === TODAY ? 'ss-month__day--today' : '',
          ]
            .filter(Boolean)
            .join(' ');
          return (
            <span key={`day-${day}`} className={classes}>
              {day}
            </span>
          );
        })}
      </div>

      <div className='ss-trade'>
        <span className='ss-trade__check' aria-hidden='true'>
          ✓
        </span>
        <div>
          <p className='ss-trade__title'>Trade approved</p>
          <p className='ss-trade__sub'>Fri 17 → Sat 18 · both parents agreed</p>
        </div>
      </div>

      <div className='ss-next'>
        <span>
          <strong>Next handoff</strong>
          Fri · 5:30 PM
        </span>
        <span className='ss-next__tag'>You</span>
      </div>
    </div>
  );
}

function ExpensesScene() {
  return (
    <div className='ss-scene ss-expenses'>
      <header className='ss-expenses__head'>
        <p className='ss-kicker'>Shared expenses</p>
        <p className='ss-scene-title'>October</p>
      </header>

      <div className='ss-balance'>
        <p className='ss-balance__label'>You&apos;re owed</p>
        <p className='ss-balance__amount'>$21.00</p>
        <div className='ss-balance__foot'>
          <span>50/50 split</span>
          <span>2 open items</span>
        </div>
      </div>

      <ul className='ss-expenses__list'>
        <li className='ss-expense'>
          <div className='ss-expense__row'>
            <span className='ss-expense__badge' aria-hidden='true'>
              S
            </span>
            <div className='ss-expense__main'>
              <p className='ss-expense__name'>Soccer cleats</p>
              <p className='ss-expense__meta'>You paid · Oct 12</p>
            </div>
            <div className='ss-expense__amount'>
              <span>$58.00</span>
              <span className='ss-expense__delta ss-expense__delta--pos'>
                +$29.00
              </span>
            </div>
          </div>
          <div className='ss-expense__chips'>
            <span className='ss-chip'>50/50</span>
            <span className='ss-chip ss-chip--muted'>Receipt attached</span>
          </div>
        </li>

        <li className='ss-expense'>
          <div className='ss-expense__row'>
            <span className='ss-expense__badge' aria-hidden='true'>
              T
            </span>
            <div className='ss-expense__main'>
              <p className='ss-expense__name'>Team snack</p>
              <p className='ss-expense__meta'>Maya paid · Oct 18</p>
            </div>
            <div className='ss-expense__amount'>
              <span>$16.00</span>
              <span className='ss-expense__delta ss-expense__delta--neg'>
                -$8.00
              </span>
            </div>
          </div>
          <div className='ss-expense__chips'>
            <span className='ss-chip'>50/50</span>
            <span className='ss-chip ss-chip--muted'>Receipt attached</span>
          </div>
        </li>
      </ul>

      <div className='ss-settle'>Request $21.00</div>
    </div>
  );
}

function SceneBody({ scene }: { scene: SceneKey }) {
  if (scene === 'chat') return <ChatScene />;
  if (scene === 'calendar') return <CalendarScene />;
  return <ExpensesScene />;
}

export function ScenarioSwapDemo({
  hero,
  syncUrl = true,
}: {
  hero?: string;
  syncUrl?: boolean;
}) {
  const navigate = useNavigate();
  const reducedMotion = usePrefersReducedMotion();
  const [scene, setScene] = useState<SceneKey>(() => resolveScene(hero));
  const [previous, setPrevious] = useState<SceneKey | null>(null);
  const heroScene = resolveScene(hero);
  const appliedHero = useRef(heroScene);

  useEffect(() => {
    if (heroScene === appliedHero.current) return;
    appliedHero.current = heroScene;
    if (heroScene === scene) return;
    if (!reducedMotion) setPrevious(scene);
    setScene(heroScene);
  }, [heroScene, scene, reducedMotion]);

  useEffect(() => {
    if (!previous) return;
    if (reducedMotion) {
      setPrevious(null);
      return;
    }
    const timeout = window.setTimeout(() => setPrevious(null), 460);
    return () => window.clearTimeout(timeout);
  }, [previous, reducedMotion]);

  const selectScene = (next: SceneKey) => {
    if (next === scene) return;
    if (reducedMotion) {
      setPrevious(null);
    } else {
      setPrevious(scene);
    }
    setScene(next);
    if (syncUrl) {
      void navigate({
        to: '/poc/swap',
        search: { hero: next },
        replace: true,
      });
    }
  };

  const copy = SCENE_COPY[scene];
  const sceneNumber = String(SCENE_ORDER.indexOf(scene) + 1).padStart(2, '0');

  return (
    <div className='ss-demo'>
      <div className='ss-demo__inner'>
        <header className='ss-intro'>
          <p className='ss-intro__eyebrow'>
            Proof of concept 04 · Scenario swap
          </p>
          <h1 className='ss-intro__title'>One frame. Any story.</h1>
          <p className='ss-intro__lede'>
            What this tests: hero variants become swappable scenes in one frame
            — faster iteration, lighter pages than 400-900KB PNGs, no layout
            shift.
          </p>
          <p className='ss-intro__hint'>
            Try the pills below, or open <code>/poc/swap?hero=calendar</code>{' '}
            and <code>/poc/swap?hero=expenses</code>. Only the story changes.
          </p>
        </header>

        <section className='ss-hero' aria-label='Scenario swap demo'>
          <div className='ss-hero__copy' aria-live='polite'>
            <div key={scene} className='ss-copy'>
              <p className='ss-copy__eyebrow'>{copy.kicker}</p>
              <h2 className='ss-copy__title'>{copy.title}</h2>
              <p className='ss-copy__sub'>{copy.subtitle}</p>
              <p className='ss-copy__meta'>
                <span className='ss-copy__dot' aria-hidden='true' />
                {copy.meta} · scene {sceneNumber} of 03
              </p>
            </div>

            <div
              className='ss-pills'
              role='group'
              aria-label='Choose a scenario'
            >
              {SCENE_ORDER.map((key, index) => (
                <button
                  key={key}
                  type='button'
                  className='ss-pill'
                  aria-pressed={scene === key}
                  onClick={() => selectScene(key)}
                >
                  <span className='ss-pill__index' aria-hidden='true'>
                    {String(index + 1).padStart(2, '0')}
                  </span>
                  {SCENE_COPY[key].label}
                </button>
              ))}
            </div>
          </div>

          <div className='ss-hero__device'>
            <PhoneFrame size='lg' glow label='Same frame · three stories'>
              <div className='ss-stage'>
                {previous && !reducedMotion ? (
                  <div
                    key={`out-${previous}`}
                    className='ss-layer ss-layer--out'
                    aria-hidden='true'
                    onAnimationEnd={(event) => {
                      if (event.target === event.currentTarget) {
                        setPrevious(null);
                      }
                    }}
                  >
                    <SceneBody scene={previous} />
                  </div>
                ) : null}
                <div key={`in-${scene}`} className='ss-layer ss-layer--in'>
                  <SceneBody scene={scene} />
                </div>
              </div>
            </PhoneFrame>
          </div>
        </section>
      </div>
    </div>
  );
}
